import { useEffect, useRef } from 'react';
import { SITE_URL } from '@/config/site';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  ogImageType?: string;
  ogType?: string;
  ogSiteName?: string;
  ogLocale?: string;
  ogSeeAlso?: string[];
  twitterCard?: string;
  twitterCreator?: string;
  twitterImageAlt?: string;
  schema?: object[];
  noindex?: boolean;
  heroImagePreload?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  ogImageType,
  ogType = 'website',
  ogSiteName = 'CUBE MC',
  ogLocale,
  ogSeeAlso,
  twitterCard = 'summary_large_image',
  twitterCreator,
  twitterImageAlt,
  schema,
  noindex = false,
  heroImagePreload,
}: SEOProps) {
  const prevHeroRef = useRef<HTMLLinkElement | null>(null);

  /* ── hero image preload (client-side only) ── */
  useEffect(() => {
    if (prevHeroRef.current && prevHeroRef.current.parentNode) {
      prevHeroRef.current.parentNode.removeChild(prevHeroRef.current);
      prevHeroRef.current = null;
    }
    if (!heroImagePreload) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = heroImagePreload;
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('data-hero-preload', 'true');
    document.head.appendChild(link);
    prevHeroRef.current = link;

    return () => {
      if (prevHeroRef.current && prevHeroRef.current.parentNode) {
        prevHeroRef.current.parentNode.removeChild(prevHeroRef.current);
        prevHeroRef.current = null;
      }
    };
  }, [heroImagePreload]);

  const canonicalHref = canonical
    ? canonical.startsWith('http')
      ? canonical
      : `${SITE_URL}${canonical}`
    : null;

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {canonicalHref && <link rel="canonical" href={canonicalHref} />}
      <meta name="last-modified" content="2026-04-30" />

      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={ogSiteName} />
      {canonicalHref && <meta property="og:url" content={canonicalHref} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:secure_url" content={ogImage} />}
      {ogImageAlt && <meta property="og:image:alt" content={ogImageAlt} />}
      {ogImageWidth && <meta property="og:image:width" content={ogImageWidth} />}
      {ogImageHeight && <meta property="og:image:height" content={ogImageHeight} />}
      {ogImageType && <meta property="og:image:type" content={ogImageType} />}
      {ogLocale && <meta property="og:locale" content={ogLocale} />}
      {ogSeeAlso && ogSeeAlso.map((url) => (
        <meta key={url} property="og:see_also" content={url} />
      ))}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content="@cubemcav" />
      {twitterCreator && <meta name="twitter:creator" content={twitterCreator} />}
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      {(twitterImageAlt || ogImageAlt) && (
        <meta name="twitter:image:alt" content={twitterImageAlt || ogImageAlt} />
      )}

      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* JSON-LD schema — rendered as static script tags in the React tree
          so they appear in DOM on first render, not injected post-hydration */}
      {schema && schema.map((schemaObj, idx) => (
        <script
          key={`seo-schema-${idx}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObj) }}
        />
      ))}
    </>
  );
}
